/**
 * ConfidenceStars Component
 *
 * Displays prediction confidence as a star rating (1-5 stars).
 * Used in public profiles and share cards to communicate prediction quality
 * without exposing technical confidence scores.
 *
 * Philosophy: Never say "not enough data". Instead, show what we have
 * with a quality indicator (stars).
 */

import PropTypes from 'prop-types'

const ConfidenceStars = ({ confidence = null, showLabel = true, size = 'md', className = '' }) => {
  // If no confidence data or zero stars, show nothing
  if (!confidence || confidence.stars === 0) {
    return null
  }

  const { stars, label } = confidence
  const maxStars = 5

  // Size classes for stars
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  const starSize = sizeClasses[size] || sizeClasses.md

  // Color based on confidence level
  const getStarColor = (index) => {
    if (index >= stars) {
      return 'text-gray-300 dark:text-gray-600' // Empty star
    }
    // Filled star colors based on total stars
    if (stars >= 4) return 'text-yellow-400'
    if (stars >= 3) return 'text-blue-400'
    if (stars >= 2) return 'text-green-400'
    return 'text-gray-400' // 1 star - early estimate
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {/* Stars */}
      <div
        className="flex items-center"
        role="img"
        aria-label={`${stars} out of ${maxStars} stars - ${label}`}
      >
        {[...Array(maxStars)].map((_, index) => (
          <svg
            key={index}
            className={`${starSize} ${getStarColor(index)}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>

      {/* Label */}
      {showLabel && label && (
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{label}</span>
      )}
    </div>
  )
}

ConfidenceStars.propTypes = {
  /** Confidence object with stars (1-5) and label */
  confidence: PropTypes.shape({
    level: PropTypes.string,
    stars: PropTypes.number.isRequired,
    label: PropTypes.string,
    marginOfError: PropTypes.number
  }),
  /** Whether to show the text label */
  showLabel: PropTypes.bool,
  /** Size variant: sm, md, lg */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Additional CSS classes */
  className: PropTypes.string
}

export default ConfidenceStars
